import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import FinboxModal from '../FinboxModal/FinboxModal';
import { bankAccountsUpdate, getFinboxRedirectUrl } from '../../../../../store/company/apiHelpers';
import UploadCard from '../UploadCard/UploadCard';
import JourneyUploadCard from '../JourneyUploadCard/JourneyUploadCard';
import { getPlatformUserId } from '../../../../../utils/utils';
import FetchButton from './components/FetchButton/FetchButton';
import { FILE_FORMATS } from '../UploadCard/utils/utils';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { COMPANY_STATEMENT_UPLOAD } from '../../../../../utils/constants/mixpanelEvents/companyEvents';
import { useAppSelector } from '../../../../../app/hooks';
import { JOURNEY_CARD_COPY } from '../../constants';
import { JourneyUploadProps } from '../../useJourneyUploadRouting';
import useRefetchOnMagicUploadClose from '../../useRefetchOnMagicUploadClose';

export const ENTITY_REF_ID = 'entityReferenceId';

export const ENTITY_TYPE_VALUES = {
    SDK: 'SDK',
    MANUAL: 'MANUAL',
} as const;

export const FETCH_BTN_STATES = {
    INITIAL: 'INITIAL',
    SHOW_FINBOX: 'SHOW_FINBOX',
    EXIT_FINBOX: 'EXIT_FINBOX',
    UPLOAD_MANUALLY: 'UPLOAD_MANUALLY',
    CONNECTED: 'CONNECTED',
};

const { INITIAL, SHOW_FINBOX, EXIT_FINBOX, CONNECTED } = FETCH_BTN_STATES;

/**
 * Flattens every non-deleted bank statement across all account rows for the read-only
 * list under Card 1 (RECUR-28067 B2 — the per-account form is gone, so statements are
 * no longer grouped by account here).
 */
const flattenBankStatements = (bankAccounts: any[] = []) =>
    (bankAccounts || []).flatMap((account) => (account?.bankStatements || []).filter((file) => !file?.isDeleted));

function BankAccounts({
    bankAccounts,
    assesseeOrgId,
    fetchOnboardingData,
    categoryId,
    flowId,
    isInvestor,
    journeyUploadProps = {} as Partial<JourneyUploadProps>,
}) {
    const [fetchButtonState, setFetchButtonState] = useState(INITIAL);
    const [finboxUrl, setFinboxUrl] = useState(null);
    const user = useAppSelector((state) => state.user);

    useRefetchOnMagicUploadClose(fetchOnboardingData, !!journeyUploadProps?.isMagicUploadRouted);

    useEffect(() => {
        if (!finboxUrl) getFinboxRedirectingUrl();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finboxUrl]);

    useEffect(() => {
        const hasConnectedAccount = (bankAccounts || []).some((account) => account?.[ENTITY_REF_ID]);
        if (hasConnectedAccount) setFetchButtonState(CONNECTED);
    }, [bankAccounts]);

    const getFinboxRedirectingUrl = async () => {
        const obj = {
            from_date: dayjs().subtract(11, 'months').format('01/MM/YYYY'),
            to_date: dayjs().subtract(1, 'days').format('DD/MM/YYYY'),
            link_id: assesseeOrgId,
            redirect_url: window.location.origin,
        };
        const redirect_url = await getFinboxRedirectUrl(obj);
        redirect_url && setFinboxUrl(redirect_url);
    };

    // B1/B2 — no bankName/bankId anywhere: the Finbox success save and the manual statement
    // save both send only what aica needs (assesseeOrgId + entityType, D9). Pattern:
    // RawBankStatements.updateRawBankStatement, which already saves without a bank name.
    const saveBankAccount = async (payload: Record<string, unknown>) => {
        await bankAccountsUpdate(
            {
                assesseeOrgId,
                pnoId: user.userData.investorOrgId,
                entityDetail: ENTITY_TYPE_VALUES.MANUAL,
                ...payload,
            },
            {
                onSuccess: () => {
                    fetchOnboardingData();
                },
            },
        );
    };

    const onSuccess = (payload: any) => {
        setFetchButtonState(CONNECTED);
        saveBankAccount({
            entityReferenceId: payload.entityId,
            entityType: ENTITY_TYPE_VALUES.SDK,
        });
    };

    const onExit = () => {
        setFetchButtonState(EXIT_FINBOX);
    };

    const onError = () => {
        toast.error("Couldn't connect net-banking. Try again or upload statements.");
    };

    const saveBankStatement = async (fileData: any) => {
        await saveBankAccount({
            entityType: ENTITY_TYPE_VALUES.MANUAL,
            bankStatement: {
                docId: fileData?.docId,
                docName: fileData?.fileName,
                isDeleted: false,
                userId: getPlatformUserId(),
            },
        });
        Mixpanel.track(COMPANY_STATEMENT_UPLOAD);
    };

    const deleteBankStatement = async (fileData: any) => {
        await saveBankAccount({
            entityType: ENTITY_TYPE_VALUES.MANUAL,
            bankStatement: {
                docId: fileData?.docId,
                docName: fileData?.docName,
                isDeleted: true,
                userId: getPlatformUserId(),
            },
        });
    };

    return (
        <>
            <JourneyUploadCard
                title={JOURNEY_CARD_COPY.BANK_STATEMENT.TITLE}
                isInvestor={isInvestor}
                subtitle={
                    journeyUploadProps?.isMagicUploadRouted
                        ? JOURNEY_CARD_COPY.BANK_STATEMENT.SUBTITLE_MU_ON
                        : JOURNEY_CARD_COPY.BANK_STATEMENT.SUBTITLE_MU_OFF
                }
            >
                <UploadCard
                    allowMultiple
                    disableDelete
                    assesseeOrgId={assesseeOrgId}
                    acceptedFormats={FILE_FORMATS.PDF}
                    categoryId={categoryId}
                    flowId={flowId}
                    existingFiles={flattenBankStatements(bankAccounts)}
                    metadataUpdate={saveBankStatement}
                    fileDeleteHandler={deleteBankStatement}
                    {...journeyUploadProps}
                />
            </JourneyUploadCard>

            <JourneyUploadCard title={JOURNEY_CARD_COPY.BANK_STATEMENT.FETCH_TITLE} isInvestor={isInvestor} layout="row">
                <FetchButton compact fetchButtonState={fetchButtonState} setFetchButtonState={setFetchButtonState} disabled={false} />
            </JourneyUploadCard>

            {fetchButtonState === SHOW_FINBOX && (
                <FinboxModal showFinbox finboxUrl={finboxUrl} onSuccess={onSuccess} onExit={onExit} onError={onError} />
            )}
        </>
    );
}

export default BankAccounts;
